import { convertDateToYYYYMMDDFormat } from '@repo/ui';
import { getProfileTypeFromName } from 'domain/value-objects/profile-type';
import { clearProfessionalOverviewData } from 'presentation/config/store/professionals/professional-overview-slice';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Card, Row } from 'reactstrap';

import { ActivitiesComparisonWidget } from '../../components/activities-comparison-widget';
import { ProfessionalActivitiesQtyPerDayChart } from '../../components/professional-activities-qty-per-day-chart';
import { ProfessionalOverviewFilters } from '../../components/professional-overview-filters';
import { WorkloadWidget } from '../../components/workload-widget';
import { useProfessionalOverview } from '../../hooks/useProfessionalOverview';

export const ProfissionalOverviewDetails = () => {
  const dispatch = useDispatch();

  const {
    id,
    groupsUser,
    validation,
    statusStates,
    semestersUser,
    layoutModeType,
    filterBySemester,
    professionalType,
    curricularUnitsUser,
    professionalOverviewData,
    filterByCurricularUnit,
    getLegends,
    requestGetAllGroupsUser,
    requestGetAllSemestersUser,
    requestGetProfessionalById,
    requestGetProfessionalOverview,
    requestGetAllCurricularUnitsUser,
  } = useProfessionalOverview();

  const workloadWidgets = [
    {
      label: 'CH validada',
      value: {
        hours: professionalOverviewData?.validatedWorkload?.hours,
        minutes: professionalOverviewData?.validatedWorkload?.minutes,
      },
      icon: 'ri-calendar-check-line',
      color: 'success',
    },
    {
      label: 'CH rejeitada',
      value: {
        hours: professionalOverviewData?.rejectedWorkload?.hours,
        minutes: professionalOverviewData?.rejectedWorkload?.minutes,
      },
      icon: 'ri-calendar-close-line',
      color: 'danger',
    },
    {
      label: 'CH pendente',
      value: {
        hours: professionalOverviewData?.pendingWorkload?.hours,
        minutes: professionalOverviewData?.pendingWorkload?.minutes,
      },
      icon: 'ri-hourglass-fill',
      color: 'warning',
    },
  ];

  const activitiesComparisonWidgets = [
    {
      title: 'Hoje',
      values: professionalOverviewData?.validatedActivitiesOnMonth?.today,
    },
    {
      title: 'Esta Semana',
      values: professionalOverviewData?.validatedActivitiesOnMonth?.thisWeek,
    },
    {
      title: 'Este Mês',
      values: professionalOverviewData?.validatedActivitiesOnMonth?.thisMonth,
    },
    {
      title: 'Este Ano',
      values: professionalOverviewData?.validatedActivitiesOnMonth?.thisYear,
    },
  ];

  useEffect(() => {
    requestGetProfessionalById({
      id,
      professionalType: getProfileTypeFromName(professionalType),
    });
    requestGetAllGroupsUser();
    requestGetAllCurricularUnitsUser();
    requestGetAllSemestersUser();

    return () => {
      dispatch(clearProfessionalOverviewData());
    };
  }, []);

  useEffect(() => {
    const period = validation.values.period.map((date) =>
      convertDateToYYYYMMDDFormat(date),
    );

    if (period[0] && period[1]) {
      const valuesToSubmit = {
        id,
        starts: period[0],
        ends: period[1],
        groups: validation.values.groups.map((group) => group.value),
        curricularUnit: validation.values.curricularUnit?.value,
        semester: validation.values.semester?.value,
      };

      requestGetProfessionalOverview(valuesToSubmit);
    }
  }, [validation.values]);

  return (
    <Col>
      <Card className="p-3 mt-5">
        <ProfessionalOverviewFilters
          validation={validation}
          getLegends={getLegends}
          filterBySemester={filterBySemester}
          filterByCurricularUnit={filterByCurricularUnit}
          groupsUser={groupsUser}
          curricularUnitsUser={curricularUnitsUser}
          semestersUser={semestersUser}
        />
        <Row className="mt-3">
          {workloadWidgets.map((widget, idx) => {
            return (
              <WorkloadWidget
                key={idx}
                widgetData={widget}
                statusPage={statusStates.professionalOverviewDataStatus}
                darkMode={layoutModeType}
              />
            );
          })}
        </Row>

        <Row>
          <Col>
            <div className="shadow-sm border border-2 rounded-1 p-4">
              <Row className="mb-2">
                <h5 className="fw-semibold">Atividades Validadas</h5>
                <p className="text-muted fw-medium">
                  Acompanhamento das atividades validadas durante o período com
                  comparativo com períodos anteriores.
                </p>
              </Row>

              <Row>
                {activitiesComparisonWidgets.map((widget, idx) => {
                  return (
                    <ActivitiesComparisonWidget
                      key={idx}
                      title={widget.title}
                      values={widget.values}
                      statusPage={statusStates.professionalOverviewDataStatus}
                      darkMode={layoutModeType}
                    />
                  );
                })}
              </Row>

              <Row className="mt-3 me-5">
                <ProfessionalActivitiesQtyPerDayChart
                  data={
                    professionalOverviewData?.validatedActivitiesOnMonth?.series
                  }
                />
              </Row>
            </div>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};
